import { motion } from "framer-motion";
import { useLenis } from "lenis/react";
import {
  ArrowRight, Handshake, Briefcase, TrendingDown, Globe2, Search,
  Rocket, PlaneTakeoff, BarChart3, LifeBuoy, Database, Layers,
} from "lucide-react";

const ICONS = {
  broker: Handshake,
  fractional: Briefcase,
  cost: TrendingDown,
  "euro-rep": Globe2,
  finder: Search,
  npi: Rocket,
  expansion: PlaneTakeoff,
  benchmark: BarChart3,
  crisis: LifeBuoy,
  intelligence: Database,
};

export default function Services({ services }) {
  const lenis = useLenis();
  const onContact = () =>
    lenis ? lenis.scrollTo("#contact", { offset: -80 }) : document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="services" className="relative bg-cream py-16 md:py-24 lg:py-28" data-testid="services">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
        <div className="max-w-3xl mb-12 md:mb-16">
          <span className="overline text-barn">Capabilities</span>
          <h2 className="mt-4 font-display font-extrabold text-3xl md:text-4xl lg:text-5xl text-navy tracking-tight leading-[1.1]">
            A complete sourcing partner for hard-to-make parts.
          </h2>
          <p className="mt-4 font-body text-base md:text-lg text-ink leading-relaxed">
            Ten focused services across the sourcing lifecycle. Every engagement is scoped one-to-one — contact us for details.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {services?.map((s, i) => {
            const Icon = ICONS[s.id] || Layers;
            return (
              <motion.button
                key={s.id}
                onClick={onContact}
                data-testid={`service-card-${s.id}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.55, delay: (i % 3) * 0.07, ease: [0.16, 1, 0.3, 1] }}
                className="group relative text-left bg-white border border-gold-subtle rounded-lg p-6 md:p-7 flex flex-col overflow-hidden hover:border-barn/40 hover:shadow-[0_18px_40px_-22px_rgba(20,39,58,0.3)] hover:-translate-y-1 transition-[transform,box-shadow,border-color] duration-300"
              >
                <span className="absolute top-0 left-0 h-1 w-0 bg-barn group-hover:w-full transition-[width] duration-400" />
                <div className="flex items-center justify-between mb-5">
                  <span className="w-12 h-12 rounded-lg bg-navy flex items-center justify-center group-hover:bg-barn transition-colors duration-300">
                    <Icon size={22} className="text-white" strokeWidth={1.8} />
                  </span>
                  <span className="font-display font-bold text-sm text-navy-soft/50">{s.number}</span>
                </div>
                <h3 className="font-display font-bold text-lg md:text-xl text-navy leading-snug">{s.title}</h3>
                <p className="mt-2.5 font-body text-sm text-ink leading-relaxed flex-1">{s.description}</p>
                <span className="mt-5 inline-flex items-center gap-1.5 font-body text-sm font-bold text-barn">
                  Contact for info <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform duration-300" />
                </span>
              </motion.button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
